<template>
  <b-modal
    id="modal-upload-rate-detail"
    :title="`上傳率詳情 - ${supplier ? supplier.company_name_cn : ''}`"
    size="xl"
    hide-footer
    scrollable
  >
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary" label="載入中..."></b-spinner>
      <p class="mt-2">正在載入上傳率詳細資料...</p>
    </div>

    <div v-else-if="uploadRateData" class="upload-rate-details">
      <!-- 基本統計信息 -->
      <b-card class="mb-3">
        <b-card-header>
          <h5 class="mb-0">
            <feather-icon icon="BarChartIcon" class="mr-2" />
            基本統計
          </h5>
        </b-card-header>
        <b-card-body>
          <b-row>
            <b-col md="3">
              <div class="text-center p-3 border rounded">
                <h4 class="text-primary mb-1">{{ uploadRateData.client_count || 0 }}</h4>
                <small class="text-muted">總客戶數</small>
              </div>
            </b-col>
            <b-col md="3">
              <div class="text-center p-3 border rounded">
                <h4 class="text-success mb-1">{{ uploadRateData.valid_client_count || 0 }}</h4>
                <small class="text-muted">有效客戶數</small>
              </div>
            </b-col>
            <b-col md="3">
              <div class="text-center p-3 border rounded">
                <h4 class="text-info mb-1">{{ uploadRatePercentage }}%</h4>
                <small class="text-muted">上傳率</small>
              </div>
            </b-col>
            <b-col md="3">
              <div class="text-center p-3 border rounded">
                <h4 class="text-warning mb-1">{{ missingRecordsCount }}</h4>
                <small class="text-muted">待上傳記錄數</small>
              </div>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- 提升上傳率指引 -->
      <b-alert v-if="missingRecordsCount > 0" show variant="warning" class="mb-3">
        <h6>
          <feather-icon icon="AlertTriangleIcon" class="mr-2" />
          如何提升上傳率
        </h6>
        <p class="mb-2">
          要提升上傳率，請為以下 <strong>{{ missingRecordsCount }}</strong> 個已搜索但未上傳記錄的客戶身份證號碼上傳相應的記錄：
        </p>
        <ul class="mb-0">
          <li>上傳貸款記錄</li>
          <li>上傳帳戶記錄</li>
          <li>上傳拒絕記錄</li>
        </ul>
      </b-alert>

      <!-- 詳細記錄分類 -->
      <b-row>
        <!-- 已搜索且有記錄的客戶 -->
        <b-col md="6">
          <b-card class="mb-3">
            <b-card-header class="bg-success text-white">
              <h6 class="mb-0">
                <feather-icon icon="CheckCircleIcon" class="mr-2" />
                已有記錄客戶 ({{ (uploadRateData.valid_in_search || []).length }})
              </h6>
            </b-card-header>
            <b-card-body style="max-height: 300px; overflow-y: auto;">
              <p class="text-muted small mb-2">
                這些客戶已被搜索且有相應的記錄（貸款/帳戶/拒絕記錄）
              </p>
              <div v-if="(uploadRateData.valid_in_search || []).length === 0" class="text-muted">
                暫無記錄
              </div>
              <div v-else>
                <b-badge
                  v-for="id in uploadRateData.valid_in_search"
                  :key="id"
                  variant="success"
                  class="mr-1 mb-1"
                >
                  {{ id }}
                </b-badge>
              </div>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- 需要上傳記錄的客戶 -->
        <b-col md="6">
          <b-card class="mb-3">
            <b-card-header class="bg-warning text-dark">
              <h6 class="mb-0">
                <feather-icon icon="AlertCircleIcon" class="mr-2" />
                需要上傳記錄的客戶 ({{ (uploadRateData.missing_records || []).length }})
              </h6>
            </b-card-header>
            <b-card-body style="max-height: 300px; overflow-y: auto;">
              <p class="text-muted small mb-2">
                <strong>重點提升項目：</strong>這些客戶已被搜索但缺少記錄，為他們上傳任何類型的記錄（貸款/帳戶/拒絕）都能直接提升上傳率
              </p>
              <div v-if="(uploadRateData.missing_records || []).length === 0" class="text-muted">
                🎉 恭喜！所有搜索的客戶都已有記錄
              </div>
              <div v-else>
                <b-badge
                  v-for="id in uploadRateData.missing_records"
                  :key="id"
                  variant="warning"
                  class="mr-1 mb-1"
                >
                  {{ id }}
                </b-badge>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- 其他記錄統計 -->
      <b-row>
        <!-- 有記錄但未搜索的客戶 -->
        <b-col md="12">
          <b-card class="mb-3">
            <b-card-header class="bg-info text-white">
              <h6 class="mb-0">
                <feather-icon icon="FileTextIcon" class="mr-2" />
                有記錄但未搜索的客戶 ({{ (uploadRateData.valid_not_in_search || []).length }})
              </h6>
            </b-card-header>
            <b-card-body style="max-height: 200px; overflow-y: auto;">
              <p class="text-muted small mb-2">
                這些客戶有記錄但從未被搜索過，不影響上傳率計算
              </p>
              <div v-if="(uploadRateData.valid_not_in_search || []).length === 0" class="text-muted">
                暫無記錄
              </div>
              <div v-else>
                <b-badge
                  v-for="id in uploadRateData.valid_not_in_search"
                  :key="id"
                  variant="info"
                  class="mr-1 mb-1"
                >
                  {{ id }}
                </b-badge>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <div v-else class="text-center py-4">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-muted mb-3" />
      <p class="text-muted">無法載入上傳率詳細資料</p>
    </div>

    <template #modal-footer>
      <b-button variant="secondary" @click="$bvModal.hide('modal-upload-rate-detail')">
        關閉
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BAlert,
  BBadge,
} from "bootstrap-vue";
import { ref, computed } from "@vue/composition-api";

export default {
  components: {
    BModal,
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BAlert,
    BBadge,
  },
  props: {
    supplier: {
      type: Object,
      default: null,
    },
    uploadRateData: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const uploadRatePercentage = computed(() => {
      if (!props.uploadRateData || !props.uploadRateData.client_count) {
        return "0.00";
      }
      const rate = (props.uploadRateData.valid_client_count / props.uploadRateData.client_count) * 100;
      return rate.toFixed(2);
    });

    const missingRecordsCount = computed(() => {
      return props.uploadRateData?.missing_records?.length || 0;
    });

    return {
      uploadRatePercentage,
      missingRecordsCount,
    };
  },
};
</script>

<style scoped>
.upload-rate-details .card-header h6 {
  font-weight: 600;
}

.upload-rate-details .badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.5rem;
}

.upload-rate-details .card-body {
  font-size: 0.875rem;
}
</style>